import {Dispatch, SetStateAction, useMemo} from 'react';
import {Box, Grow, Stack, SxProps, Theme, Typography} from '@mui/material';
import {TbArrowsSort} from 'react-icons/tb';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {Checkbox} from '../forms/checkbox';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';

interface TaskSortHeaderProps {
  tasks: CareTaskComplementary[];
  selectedTasksIds: string[];
  setSelectedTasksIds: Dispatch<SetStateAction<string[]>>;
  onSortClick: () => void;
}

export const TaskSortHeader = ({
  tasks,
  selectedTasksIds,
  setSelectedTasksIds,
  onSortClick,
}: TaskSortHeaderProps) => {
  const {t} = useTranslation();

  const headerWrapperSx: SxProps<Theme> = {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '44px',
  };

  const textSx: SxProps<Theme> = {
    color: 'var(--dcm-gray)',
    fontFamily: 'Roboto',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.225px',
  };

  const isSelectableTask = ({careTask}: CareTaskComplementary) => {
    return Boolean(careTask.feature) || !careTask.markedAsHandled;
  };

  const areAllSelected = useMemo(() => {
    return (
      tasks &&
      tasks.filter((t) => {
        if (isSelectableTask(t)) {
          return t;
        } else {
          return null;
        }
      }).length === selectedTasksIds.length
    );
  }, [tasks, selectedTasksIds]);

  const areIndeterminate = useMemo(() => {
    return (
      tasks &&
      tasks.filter((t) => {
        if (isSelectableTask(t)) {
          return t;
        } else {
          return null;
        }
      }).length > selectedTasksIds.length &&
      selectedTasksIds?.length > 0
    );
  }, [tasks, selectedTasksIds]);

  const handleBulkSelect = () => {
    if (selectedTasksIds?.length > 0) {
      unselectAllTasks();
    } else {
      selectAllSelectableTasks();
    }
  };

  const selectAllSelectableTasks = () => {
    const allSelectableTasks: string[] = [];

    tasks?.forEach((task) => {
      if (isSelectableTask(task)) allSelectableTasks.push(task.careTask.id!);
    });

    setSelectedTasksIds(allSelectableTasks);
  };

  const unselectAllTasks = () => setSelectedTasksIds([]);

  return (
    <Stack sx={headerWrapperSx}>
      <Stack flexDirection="row" alignItems="center" justifyContent="center">
        <Grow in unmountOnExit>
          <Box
            sx={{
              mr: 1,
            }}
          >
            <Checkbox
              id="select-all"
              checked={areAllSelected}
              indeterminate={areIndeterminate}
              onClick={handleBulkSelect}
            />
          </Box>
        </Grow>
        <Typography component="label" htmlFor="select-all" sx={textSx}>
          {t(translations.daily_care_log.task_table.select_all)}
        </Typography>
      </Stack>

      <Stack gap={0.5} flexDirection="row" alignItems="center" onClick={onSortClick}>
        <Typography htmlFor="sort-label" component="label" sx={textSx}>
          {t(translations.daily_care_log.task_table.sort)}
        </Typography>
        <TbArrowsSort id="sort-label" size={19} color="var(--dcm-gray)" />
      </Stack>
    </Stack>
  );
};
